import React, { useCallback, useEffect, useState } from "react";
import {
  Search,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Loader2,
  Filter,
  ScrollText,
} from "lucide-react";
import { AuditResponseDTO } from "../Types/ApplicationLogs";
import { searchAuditLogs } from "../Services/ApplicationLogService";
import { formatDateTime } from "../../Shared/Utils/HelperUtils";
import Pagination from "../../Shared/Pagination/Pagination";
import {
  Badge,
  statusStyles,
  statusIcon,
  actionStyles,
  auditCategoryStyles,
  actorTypeStyles,
} from "../Utils/LogColors";

interface Filters {
  text: string;
  action: string;
  category: string;
  entityType: string;
  actorType: string;
  actorId: string;
  entityId: string;
  correlationId: string;
  success: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = {
  text: "",
  action: "",
  category: "",
  entityType: "",
  actorType: "",
  actorId: "",
  entityId: "",
  correlationId: "",
  success: "",
  from: "",
  to: "",
};

const ACTIONS = ["CREATE", "UPDATE", "DELETE", "LOGIN", "LOGOUT", "APPROVE", "ACCESS_DENIED", "API_ACCESS"];
const CATEGORIES = ["AUTH", "SECURITY", "PAYMENT", "API", "ADMIN", "PROPERTY", "SYSTEM"];
const ENTITY_TYPES = ["ADMIN", "BROKER", "PAYMENT", "PROPERTY", "SYSTEM", "USER"];
const ACTOR_TYPES = ["ADMIN", "SUPER_ADMIN", "USER", "RECRUITER", "SYSTEM"];
const SUCCESS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Success", value: "true" },
  { label: "Failed", value: "false" },
];

const selectClass =
  "w-full bg-[#0f172a] border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:border-blue-500 outline-none";
const inputClass =
  "w-full bg-[#0f172a] border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:border-blue-500 outline-none";

const LogViewerComponentForGlobalAuditLogs: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [logsData, setLogsData] = useState<AuditResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
    hasNext: false,
    hasPrevious: false,
  });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.text) params.set("text", filters.text);
    if (filters.action) params.set("action", filters.action);
    if (filters.category) params.set("category", filters.category);
    if (filters.entityType) params.set("entityType", filters.entityType);
    if (filters.actorType) params.set("actorType", filters.actorType);
    if (filters.actorId) params.set("actorId", filters.actorId);
    if (filters.entityId) params.set("entityId", filters.entityId);
    if (filters.correlationId) params.set("correlationId", filters.correlationId);
    if (filters.success !== "") params.set("success", filters.success);
    if (filters.from) params.set("from", new Date(filters.from).toISOString());
    if (filters.to) params.set("to", new Date(filters.to).toISOString());
    return params.toString();
  }, [filters]);

  const runSearch = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      const response = await searchAuditLogs(buildQuery(), targetPage, 50);
      setLoading(false);

      if (response.success && response.data) {
        setLogsData(response.data.content);
        setPagination({
          page: response.data.page,
          size: response.data.size,
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          first: response.data.first,
          last: response.data.last,
          hasNext: response.data.hasNext,
          hasPrevious: response.data.hasPrevious,
        });
      }
    },
    [buildQuery]
  );

  useEffect(() => {
    runSearch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    runSearch(0);
  };

  const handlePageChange = (p: number | ((prev: number) => number)) => {
    if (typeof p === "number") {
      runSearch(p);
    }
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-[#0f172a] text-slate-300 font-sans">
      {/* Header / Search */}
      <div className="flex-none border-b border-slate-800 px-4 py-3 flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 text-xs font-bold text-blue-400 uppercase tracking-wide">
          <ScrollText size={16} /> Audit Logs
        </span>

        <div className="flex-1 min-w-[220px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={15} />
          <input
            type="text"
            value={filters.text}
            onChange={(e) => setFilters({ ...filters, text: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && runSearch(0)}
            placeholder="Search correlation, actor, entity, action, category..."
            className="w-full bg-[#0f172a] border border-slate-700 rounded-md py-2 pl-9 pr-3 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </div>

        <button
          onClick={() => runSearch(0)}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md text-xs font-semibold"
        >
          Run
        </button>

        <button
          onClick={resetFilters}
          className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 hover:border-slate-600 text-slate-300 px-3 py-2 rounded-md text-xs font-semibold"
        >
          <RefreshCw size={13} /> Clear
        </button>

        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`md:hidden flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border transition-all ${
            showFilters || activeFilterCount > 0
              ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
              : "bg-slate-800 border-slate-700 text-slate-300"
          }`}
        >
          <Filter size={13} /> Filters
          {activeFilterCount > 0 && (
            <span className="px-1.5 rounded-full bg-blue-600 text-white text-[10px]">
              {activeFilterCount}
            </span>
          )}
          <ChevronDown
            size={13}
            className={`transition-transform ${showFilters ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* Filter bar */}
      <div
        className={`flex-none border-b border-slate-800 px-4 py-2.5 ${
          showFilters ? "grid" : "hidden"
        } md:grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-2`}
      >
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Action</label>
          <select
            className={selectClass}
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          >
            <option value="">All</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Category</label>
          <select
            className={selectClass}
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          >
            <option value="">All</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Entity type</label>
          <select
            className={selectClass}
            value={filters.entityType}
            onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
          >
            <option value="">All</option>
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Actor type</label>
          <select
            className={selectClass}
            value={filters.actorType}
            onChange={(e) => setFilters({ ...filters, actorType: e.target.value })}
          >
            <option value="">All</option>
            {ACTOR_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">Result</label>
          <select
            className={selectClass}
            value={filters.success}
            onChange={(e) => setFilters({ ...filters, success: e.target.value })}
          >
            {SUCCESS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">From</label>
          <input
            type="datetime-local"
            className={inputClass}
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase text-slate-500 mb-1">To</label>
          <input
            type="datetime-local"
            className={inputClass}
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          />
        </div>
        <div className="col-span-2 md:col-span-3 xl:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="block text-[9px] uppercase text-slate-500 mb-1">Actor id</label>
            <input
              className={inputClass}
              value={filters.actorId}
              placeholder="actor id"
              onChange={(e) => setFilters({ ...filters, actorId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-[9px] uppercase text-slate-500 mb-1">Entity id</label>
            <input
              className={inputClass}
              value={filters.entityId}
              placeholder="entity id"
              onChange={(e) => setFilters({ ...filters, entityId: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-[9px] uppercase text-slate-500 mb-1">Correlation id</label>
            <input
              className={inputClass}
              value={filters.correlationId}
              placeholder="correlation id"
              onChange={(e) => setFilters({ ...filters, correlationId: e.target.value })}
            />
          </div>
          <div className="flex items-end">
            <div className="text-[10px] text-slate-500 py-1.5">
              {logsData.length} on page · {pagination.totalElements} total
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-auto min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-500">
            <Loader2 size={22} className="animate-spin mr-2" /> Loading audit logs...
          </div>
        ) : logsData.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-600">
            <Filter size={28} className="mr-2 opacity-40" />
            {activeFilterCount > 0 ? "No events match the current filters." : "No audit logs recorded yet."}
          </div>
        ) : (
          <div className="p-3">
            {/* Desktop table */}
            <table className="hidden md:table w-full text-left border-collapse">
              <thead>
                <tr className="text-slate-500 text-[10px] uppercase border-b border-slate-800">
                  <th className="pb-2 pl-2 w-8"></th>
                  <th className="pb-2 font-semibold">Time</th>
                  <th className="pb-2 font-semibold text-center">Result</th>
                  <th className="pb-2 font-semibold">Action</th>
                  <th className="pb-2 font-semibold">Category</th>
                  <th className="pb-2 font-semibold">Actor</th>
                  <th className="pb-2 font-semibold">Entity</th>
                  <th className="pb-2 font-semibold">Correlation id</th>
                </tr>
              </thead>
              <tbody className="text-xs font-mono">
                {logsData.map((log) => (
                  <AuditRow
                    key={log.id}
                    log={log}
                    isExpanded={expandedLog === log.id}
                    onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                  />
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="md:hidden flex flex-col divide-y divide-slate-800">
              {logsData.map((log) => (
                <MobileAuditCard
                  key={log.id}
                  log={log}
                  isExpanded={expandedLog === log.id}
                  onToggle={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                />
              ))}
            </div>

            <Pagination
              page={pagination.page}
              size={pagination.size || 50}
              totalElements={pagination.totalElements}
              totalPages={pagination.totalPages}
              first={pagination.first}
              last={pagination.last}
              hasNext={pagination.hasNext}
              hasPrevious={pagination.hasPrevious}
              setPage={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const AuditRow: React.FC<{
  log: AuditResponseDTO;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ log, isExpanded, onToggle }) => (
  <React.Fragment>
    <tr className="border-b border-slate-800/60 hover:bg-slate-800/20 cursor-pointer transition-colors">
      <td className="py-2 pl-2" onClick={onToggle}>
        {isExpanded ? (
          <ChevronDown size={14} className="text-slate-400" />
        ) : (
          <ChevronRight size={14} className="text-slate-500" />
        )}
      </td>
      <td className="py-2 text-slate-400 whitespace-nowrap" onClick={onToggle}>
        {formatDateTime(log.timestamp, "dateTimeWithSeconds")}
      </td>
      <td className="py-2 text-center" onClick={onToggle}>
        {log.success ? (
          <Badge label="SUCCESS" className={statusStyles.SUCCESS} icon={statusIcon("SUCCESS")} />
        ) : (
          <Badge label="FAILED" className={statusStyles.FAILED} icon={statusIcon("FAILED")} />
        )}
      </td>
      <td className="py-2" onClick={onToggle}>
        <Badge label={log.action || "-"} className={actionStyles[log.action] || "bg-slate-500/10 text-slate-300 border-slate-500/30"} />
      </td>
      <td className="py-2" onClick={onToggle}>
        <Badge label={log.category || "-"} className={auditCategoryStyles[log.category] || "bg-slate-500/10 text-slate-300 border-slate-500/30"} />
      </td>
      <td className="py-2" onClick={onToggle}>
        {log.actorId ? (
          <span className="inline-flex items-center gap-1">
            <Badge label={log.actorType || "SYSTEM"} className={actorTypeStyles[log.actorType] || "bg-slate-500/10 text-slate-300 border-slate-500/30"} />
            <span className="text-slate-400">{log.actorId}</span>
          </span>
        ) : (
          "-"
        )}
      </td>
      <td className="py-2 text-[10px] text-slate-500 max-w-[180px] truncate" title={log.entityId} onClick={onToggle}>
        {log.entityType ? `${log.entityType} ${log.entityId || ""}`.trim() : "-"}
      </td>
      <td className="py-2 text-slate-500 max-w-[200px] truncate" title={log.correlationId} onClick={onToggle}>
        {log.correlationId || "-"}
      </td>
    </tr>
    {isExpanded && (
      <tr className="bg-slate-900/60">
        <td colSpan={8} className="px-4 py-3">
          <div className="grid gap-3 text-xs">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-slate-400">
              <KV k="Actor id" v={log.actorId} />
              <KV k="Actor type" v={log.actorType} />
              <KV k="Entity" v={log.entityType ? `${log.entityType} ${log.entityId || ""}`.trim() : undefined} />
              <KV k="Correlation id" v={log.correlationId} />
              <KV k="Event id" v={log.id} />
            </div>
            {log.payload && (
              <div>
                <div className="text-[9px] uppercase text-slate-500 mb-1">Payload</div>
                <pre className="bg-slate-950 border border-slate-800 rounded-md p-2 text-[11px] text-slate-300 whitespace-pre-wrap break-words font-mono max-h-64 overflow-auto">
                  {prettyPayload(log.payload)}
                </pre>
              </div>
            )}
          </div>
        </td>
      </tr>
    )}
  </React.Fragment>
);

const MobileAuditCard: React.FC<{
  log: AuditResponseDTO;
  isExpanded: boolean;
  onToggle: () => void;
}> = ({ log, isExpanded, onToggle }) => (
  <div className="py-3 cursor-pointer" onClick={onToggle}>
    <div className="flex items-center justify-between mb-1.5 gap-2">
      <span className="text-blue-400 text-[11px] font-mono">
        {formatDateTime(log.timestamp, "dateTimeWithSeconds")}
      </span>
      {log.success ? (
        <Badge label="SUCCESS" className={statusStyles.SUCCESS} icon={statusIcon("SUCCESS")} />
      ) : (
        <Badge label="FAILED" className={statusStyles.FAILED} icon={statusIcon("FAILED")} />
      )}
    </div>
    <div className="flex items-center gap-2 flex-wrap mb-1">
      <Badge label={log.action || "-"} className={actionStyles[log.action] || "bg-slate-500/10 text-slate-300 border-slate-500/30"} />
      <Badge label={log.category || "-"} className={auditCategoryStyles[log.category] || "bg-slate-500/10 text-slate-300 border-slate-500/30"} />
      {log.actorType && (
        <Badge label={log.actorType} className={actorTypeStyles[log.actorType] || "bg-slate-500/10 text-slate-300 border-slate-500/30"} />
      )}
    </div>
    <p className="text-slate-400 text-xs truncate">
      {log.actorId ? `${log.actorId} — ` : ""}
      {log.entityType ? `${log.entityType} ${log.entityId || ""}`.trim() : ""}
    </p>
    {isExpanded && log.payload && (
      <pre className="mt-3 bg-slate-950 border border-slate-800 rounded-md p-2 text-[11px] text-slate-300 whitespace-pre-wrap break-words font-mono max-h-64 overflow-auto">
        {prettyPayload(log.payload)}
      </pre>
    )}
  </div>
);

const prettyPayload = (payload?: string): string => {
  if (!payload) return "-";
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const KV: React.FC<{ k: string; v?: string | number }> = ({ k, v }) => (
  <div className="min-w-0">
    <div className="text-[9px] uppercase text-slate-600">{k}</div>
    <div className="truncate text-slate-300">{v?.toString() || "-"}</div>
  </div>
);

export default LogViewerComponentForGlobalAuditLogs;